import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { channel } from "diagnostics_channel";
import { getLocalUserId } from "../../components/functions/utilities";
import {
  AccessUsers,
  Channel,
  Chart,
  Message,
  PivotTable,
  TrackerWithRecipientNames,
} from "../../interfaces/interfaces";
import {
  fetchAsyncChannelsAndTrackers,
  fetchAsyncMessages,
  fetchAsynDatapiece,
  updateAsyncChannelPinStatus,
  UpdateAsyncChannelUnreadStatus,
} from "./chatUtil";

export interface ChatState {
  channels: {
    [x: string]: Channel;
  };
  sortedChannelIdsByChartId: {
    [x: string]: string[];
  };
  sortedChannelList: string[];
  pinnedChannelList: string[];
  currentChannelId: string;
  channelMessages: {
    [x: string]: Message[];
  };
  messagesLoadedChannels: {
    [x: string]: boolean;
  };
  unreadMessages: {
    [x: string]: UnreadCount;
  };
  searchStatus: boolean;
  filterStatus: boolean;
  filterType: string;
  filteredChannelList: string[];
  trackers: TrackerWithRecipientNames[];
  trackerCachedList: TrackerWithRecipientNames[];
  cachedChart: {
    [x: string]: Chart;
  };
  cachedPivot: {
    [x: string]: PivotTable;
  };
  allCollaborators: {
    [x: string]: {
      [x: string]: AccessUsers;
    };
  };
  firstScroll: boolean;
  loading: boolean;
  isActiveChannelDeleted: boolean;
  trackerFilterStatus: boolean;
  enabelClickOutsideTrigger: boolean;
  error: any;
  activeTrackerId: number;
  newChannelOnChartId: number;
  isCollboration: boolean;
  joinNewChannelTrigger: string;
}

export type UnreadCount = {
  channelId: number;
  userId: number;
  unreadCount: number;
};

export type AddChannelsPayload = {
  channels: Channel[];
  unreadMessages: UnreadCount[];
  sortedChannelIds: {
    [x: string]: string[];
  };
};

type AddMessagePayload = {
  messages: Message[];
  channelId: number;
};

type AddSingleMessagePayload = {
  message: Message;
  channelId: number;
  userId: string;
};

type AddSearchChannels = {
  searchStatus: boolean;
  channelList: Channel[];
};

type ChangeCurrentChannelPayload = {
  channelId: string;
  isCollaboration: boolean;
};

type FilterActionPayload = {
  filterStatus: boolean;
  filterType: string;
};

type DeleteTrackerPayload = {
  trackerIndex: number;
  trackerId: number;
};

type DeleteChannelPayload = {
  channelId: number;
  isPinned: boolean;
};

type AllCollaboratorsPayload = {
  reportId: number;
  users: AccessUsers;
};

type NewChannelOnDataPiecePayload = {
  channelId: number;
};

type AddedUserToNewChannelPayload = {
  channel: Channel;
};

export type PinnedChatsPayload = {
  action: boolean;
  channelId: string;
};

const INITIAL_STATE = {
  channels: null,
  sortedChannelList: [],
  sortedChannelIdsByChartId: {},
  pinnedChannelList: [],
  currentChannelId: null,
  messagesLoadedChannels: {},
  channelMessages: null,
  unreadMessages: {},
  cachedChart: {},
  cachedPivot: {},
  trackers: null,
  loading: false,
  searchStatus: false,
  filteredChannelList: [],
  error: null,
  firstScroll: false,
  filterStatus: false,
  filterType: null,
  isActiveChannelDeleted: false,
  trackerFilterStatus: false,
  trackerCachedList: [],
  enabelClickOutsideTrigger: true,
  allCollaborators: {},
  activeTrackerId: null,
  newChannelOnChartId: null,
  isCollboration: false,
  joinNewChannelTrigger: null,
} as ChatState;

const ChatSlice = createSlice({
  name: "chat",
  initialState: INITIAL_STATE,
  reducers: {
    setNewChannelTrigger: (state, action: PayloadAction<boolean>) => {
      state.joinNewChannelTrigger = null;
    },
    setIsCollaboration: (state, action: PayloadAction<boolean>) => {
      state.isCollboration = action.payload;
    },
    addAllCollaborators: (
      state,
      action: PayloadAction<AllCollaboratorsPayload>
    ) => {
      state.allCollaborators = {
        ...state.allCollaborators,
        [action.payload.reportId]: action.payload.users,
      };
    },
    changeClickOutsideStatus: (state, action: PayloadAction<boolean>) => {
      state.enabelClickOutsideTrigger = action.payload;
    },
    resetIsisActiveChannelDeleted: (state) => {
      state.isActiveChannelDeleted = false;
    },
    resetFromCollaboration: (state) => {
      state.currentChannelId = null;
    },
    resetTrackerFilterStatus: (state) => {
      state.trackers = [...state.trackerCachedList];
      state.trackerFilterStatus = false;
      state.trackerCachedList = [];
    },
    // deleteTracker: (state, action: PayloadAction<DeleteTrackerPayload>) => {
    //   if (state.trackerFilterStatus) {
    //     let newTracker = [...state.trackers];
    //     let splicedTracker = newTracker.splice(action.payload.trackerIndex, 1);
    //     state.trackers = newTracker;

    //     let foundTrackerIdxInCachedList = [
    //       ...state.trackerCachedList,
    //     ].findIndex(
    //       (element) =>
    //         element.id.toString() === action.payload.trackerId.toString()
    //     );

    //
    //     if (foundTrackerIdxInCachedList !== -1) {
    //       let newCachedList = [...state.trackerCachedList];
    //       let splicedCachedTracker = newCachedList.splice(
    //         foundTrackerIdxInCachedList,
    //         1
    //       );
    //       state.trackerCachedList = newCachedList;
    //     }
    //   } else {
    //     let newTracker = [...state.trackers];
    //     let splicedTracker = newTracker.splice(action.payload.trackerIndex, 1);
    //     state.trackers = newTracker;
    //   }
    // },
    addTempChannel: (state, action: PayloadAction<Channel>) => {
      if (state.channels == null) {
        state.channels = {
          ["temp"]: action.payload,
        };
        state.currentChannelId = "temp";
      } else {
        state.channels["temp"] = action.payload;
        state.currentChannelId = "temp";
      }
    },
    changeCurrentChannel: (
      state,
      action: PayloadAction<ChangeCurrentChannelPayload>
    ) => {
      if (action.payload.isCollaboration) {
        state.firstScroll = true;
      }
      //
      state.currentChannelId = action.payload.channelId;
    },
    resetFirstScroll: (state) => {
      state.firstScroll = false;
    },
    applyFilters: (state, action: PayloadAction<FilterActionPayload>) => {
      state.filterStatus = action.payload.filterStatus;
      state.filterType = action.payload.filterType;
    },
    addChannels: (state, action: PayloadAction<AddChannelsPayload>) => {
      const channelObj = action.payload.channels.reduce((prev, currVal) => {
        prev[currVal.id.toString()] = currVal;
        return prev;
      }, {});
      const unreadCountObj = action.payload.unreadMessages.reduce(
        (prev, currVal) => {
          prev[currVal.channelId.toString()] = currVal;
          return prev;
        },
        {}
      );
      state.sortedChannelIdsByChartId = {
        ...state.sortedChannelIdsByChartId,
        ...action.payload.sortedChannelIds,
      };
      state.unreadMessages = {
        ...state.unreadMessages,
        ...unreadCountObj,
      };
      if (state.channels == null) {
        state.channels = {
          ...channelObj,
        };
      } else {
        state.channels = {
          ...state.channels,
          ...channelObj,
        };
      }
    },
    addFilteredChannels: (state, action: PayloadAction<AddSearchChannels>) => {
      const channelList = action.payload.channelList.map((item) =>
        item.id.toString()
      );
      const reducedObjectChannelList = action.payload.channelList.reduce(
        (prev, currVal) => {
          prev[`${currVal.id}`] = currVal;
          return prev;
        },
        {}
      );
      state.filteredChannelList = channelList;
      state.channels = {
        ...state.channels,
        ...reducedObjectChannelList,
      };
      state.searchStatus = action.payload.searchStatus;
    },
    addMessages: (state, action: PayloadAction<AddMessagePayload>) => {
      if (state.channelMessages == null) {
        state.channelMessages = {
          [action.payload.channelId.toString()]: action.payload.messages,
        };
      } else {
        state.channelMessages = {
          ...state.channelMessages,
          [action.payload.channelId.toString()]: action.payload.messages,
        };
      }
      state.messagesLoadedChannels =
        state.messagesLoadedChannels == null
          ? {
              [action.payload.channelId.toString()]: true,
            }
          : {
              ...state.messagesLoadedChannels,
              [action.payload.channelId.toString()]: true,
            };
    },
    createChannel: (
      state,
      action: PayloadAction<{ channel: Channel; isCollaboration: boolean }>
    ) => {
      let currentChannel = action.payload.channel;
      let isCollaboration = action.payload.isCollaboration;
      if (state.channels == null) {
        state.channels = {
          [currentChannel.id]: currentChannel,
        };
        state.currentChannelId = currentChannel.id.toString();
      } else {
        state.channels[currentChannel.id.toString()] = currentChannel;
        state.currentChannelId = currentChannel.id.toString();
      }
      if (isCollaboration) {
        state.sortedChannelList = [
          currentChannel.id.toString(),
          ...state.sortedChannelList,
        ];
      }
    },
    addSingleMessageReceived: (
      state,
      action: PayloadAction<AddSingleMessagePayload>
    ) => {
      const isPinned = state.channels[
        action.payload.channelId.toString()
      ].pin.includes(action.payload.userId.toString())
        ? true
        : false;
      if (!isPinned) {
        let toMoveChannelIdIndex = [...state.sortedChannelList].findIndex(
          (element) => element.toString() == action.payload.channelId.toString()
        );
        if (toMoveChannelIdIndex > 0) {
          let newSortedList = [...state.sortedChannelList];
          let removedChannelId = newSortedList.splice(toMoveChannelIdIndex, 1);
          state.sortedChannelList = [
            action.payload.channelId.toString(),
            ...newSortedList,
          ];
        }
      }

      if (
        action.payload.message.senderId.toString() !==
        action.payload.userId.toString()
      ) {
        state.unreadMessages[`${action.payload.channelId}`].unreadCount += 1;
      }

      state.channelMessages[`${action.payload.channelId}`] = [
        ...state.channelMessages[`${action.payload.channelId}`],
        action.payload.message,
      ];
      // if(state.currentChannelId == action.payload.channelId.toString()) {

      // }
    },
    updateChannel: (state, action: PayloadAction<Channel>) => {
      state.channels[action.payload.id.toString()] = action.payload;
    },
    deleteChannelFromMenu: (
      state,
      action: PayloadAction<DeleteChannelPayload>
    ) => {
      if (action.payload.isPinned) {
        let foundChannelIndex = [...state.pinnedChannelList].findIndex(
          (element) =>
            element.toString() === action.payload.channelId.toString()
        );
        //
        if (foundChannelIndex !== -1) {
          let newPinnedChannelList = [...state.pinnedChannelList];
          let removedChannelId = newPinnedChannelList.splice(
            foundChannelIndex,
            1
          );
          state.pinnedChannelList = newPinnedChannelList;
        }
      } else {
        let foundChannelIndex = [...state.sortedChannelList].findIndex(
          (element) => element.toString() == action.payload.channelId.toString()
        );
        //
        if (foundChannelIndex !== -1) {
          let newSortedList = [...state.sortedChannelList];
          let removedChannelId = newSortedList.splice(foundChannelIndex, 1);
          state.sortedChannelList = newSortedList;
        }
      }

      if (state.currentChannelId == action.payload.channelId.toString()) {
        //
        state.isActiveChannelDeleted = true;
      }
    },
    deleteChannelFromNotification: (state, action: PayloadAction<number>) => {
      let newchannels = { ...state.channels };
      delete newchannels[`${action.payload}`];
      state.channels = newchannels;

      if (state.currentChannelId == action.payload.toString()) {
        //
        state.isActiveChannelDeleted = true;
      }

      let foundChannelIndex = [...state.pinnedChannelList].findIndex(
        (element) => element.toString() === action.payload.toString()
      );
      //
      if (foundChannelIndex !== -1) {
        let newPinnedChannelList = [...state.pinnedChannelList];
        let removedChannelId = newPinnedChannelList.splice(
          foundChannelIndex,
          1
        );
        state.pinnedChannelList = newPinnedChannelList;
      }

      let foundSortedChannelIdx = [...state.sortedChannelList].findIndex(
        (element) => element.toString() == action.payload.toString()
      );
      //
      if (foundSortedChannelIdx !== -1) {
        let newSortedList = [...state.sortedChannelList];
        let removedChannelId = newSortedList.splice(foundSortedChannelIdx, 1);
        state.sortedChannelList = newSortedList;
      }

      if (state.currentChannelId == action.payload.toString()) {
        //
        state.isActiveChannelDeleted = true;
      }
    },
    // deleteTrackersFromNotification: (
    //   state,
    //   action: PayloadAction<{ trackerId: number[] }>
    // ) => {
    //   let toRemoveTrackerIds = action.payload.trackerId;
    //   const newTrackers = state.trackers.filter(
    //     (item) => !toRemoveTrackerIds.includes(item.id)
    //   );
    //   const newTrackerCachedList = state.trackerCachedList.filter(
    //     (item) => !toRemoveTrackerIds.includes(item.id)
    //   );
    //   state.trackers = newTrackers;
    //   state.trackerCachedList = newTrackerCachedList;
    // },
    setActiveTrackerId: (state, action: PayloadAction<number>) => {
      state.activeTrackerId = action.payload;
    },
    setNewChannelonChartId: (
      state,
      action: PayloadAction<NewChannelOnDataPiecePayload>
    ) => {
      const newChannel = state.channels[action.payload.channelId]
        ? state.channels[action.payload.channelId]
        : null;
      if (newChannel !== null) {
        let newSortedChannelByChartId =
          state.sortedChannelIdsByChartId.hasOwnProperty(newChannel.referenceId)
            ? state.sortedChannelIdsByChartId[newChannel.referenceId]
            : [];
        if (newSortedChannelByChartId.length > 5) {
          newSortedChannelByChartId[4] = newChannel.id.toString();
        } else {
          newSortedChannelByChartId = [
            newChannel.id.toString(),
            ...newSortedChannelByChartId,
          ];
        }

        state.sortedChannelIdsByChartId = {
          ...state.sortedChannelIdsByChartId,
          [`${newChannel.referenceId}`]: newSortedChannelByChartId,
        };
        state.newChannelOnChartId = newChannel.referenceId;
      }
    },
    addNewChannel: (
      state,
      action: PayloadAction<AddedUserToNewChannelPayload>
    ) => {
      const channel = action.payload.channel;
      // const channelObj = state.channels.((prev, currVal) => {
      //   prev[currVal.id.toString()] = currVal;
      //   return prev;
      // }, {});
      if (state.channels == null) {
        state.channels = {
          [`${channel.id}`]: channel,
        };
      } else {
        state.channels = {
          ...state.channels,
          [`${channel.id}`]: channel,
        };
      }

      state.sortedChannelList = [
        channel.id.toString(),
        ...state.sortedChannelList,
      ];
      state.joinNewChannelTrigger = channel.id.toString();
    },
    addDemoChannel: (state, action: PayloadAction<Channel>) => {
      if (state.channels == null) {
        state.channels = {
          demo: action.payload,
        };

        // state.currentChannelId = "temp";
      } else {
        state.channels["demo"] = action.payload;
        // state.currentChannelId = "temp";
      }
      let sampleMessage = {
        id: 0,
        channelId: 0,
        message: "This is a sample message",
        senderId: 0,
        timestamp: Date.now(),
        files: null,
        isMessage: 1,
        editedAt: null,
        deletedAt: null,
        parentMessageId: null,
        senderName: "Merito",
        replies: null,
      };

      if (state.channelMessages == null) {
        state.channelMessages = {
          demo: [sampleMessage],
        };
      } else {
        state.channelMessages = {
          ...state.channelMessages,
          demo: [sampleMessage],
        };
      }
    },
    resetNewChannelOnChartId: (state) => {
      state.newChannelOnChartId = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchAsyncMessages.fulfilled, (state, action) => {
      if (action.payload !== null) {
        //
        const userId = getLocalUserId();
        const isPinned = state.channels[
          action.payload.channelId.toString()
        ].pin.includes(userId.toString())
          ? true
          : false;
        if (!isPinned) {
          let toMoveChannelIdIndex = [...state.sortedChannelList].findIndex(
            (element) =>
              element.toString() == action.payload.channelId.toString()
          );
          if (toMoveChannelIdIndex > 0) {
            let newSortedList = [...state.sortedChannelList];
            let removedChannelId = newSortedList.splice(
              toMoveChannelIdIndex,
              1
            );
            state.sortedChannelList = [
              action.payload.channelId.toString(),
              ...newSortedList,
            ];
          }
        }
        state.channelMessages =
          state.channelMessages == null
            ? {
                [action.payload.channelId.toString()]: action.payload.messages,
              }
            : {
                ...state.channelMessages,
                [action.payload.channelId.toString()]: action.payload.messages,
              };
        state.messagesLoadedChannels = {
          ...state.messagesLoadedChannels,
          [action.payload.channelId.toString()]: true,
        };
        state.unreadMessages = {
          ...state.unreadMessages,
          ...action.payload.unreadCount,
        };
      }
    });
    builder.addCase(
      UpdateAsyncChannelUnreadStatus.fulfilled,
      (state, action) => {
        //
        if (action.payload !== null) {
          state.unreadMessages[`${action.payload.channelId}`] =
            action.payload.unreadCount[0];
        }
      }
    );
    builder.addCase(fetchAsyncChannelsAndTrackers.pending, (state, action) => {
      state.error = null;
      state.loading = true;
    });
    builder.addCase(
      fetchAsyncChannelsAndTrackers.fulfilled,
      (state, action) => {
        let combinedChannels = [
          ...action.payload.pinnedChannels,
          ...action.payload.unpinnedChannels,
        ];
        const channelObj = combinedChannels.reduce((prev, currVal) => {
          prev[currVal.id.toString()] = currVal;
          return prev;
        }, {});

        const unpinnedChannelIds = action.payload.unpinnedChannels.map(
          (item, index) => item.id.toString()
        );
        const pinnedChannelIds = action.payload.pinnedChannels.map(
          (item, index) => item.id.toString()
        );
        state.sortedChannelList = unpinnedChannelIds;
        state.pinnedChannelList = pinnedChannelIds;
        const unreadCountObj = action.payload.unreadCount.reduce(
          (prev, currVal) => {
            prev[currVal.channelId.toString()] = currVal;
            return prev;
          },
          {}
        );
        state.unreadMessages = {
          ...state.unreadMessages,
          ...unreadCountObj,
        };
        if (state.channels == null) {
          state.channels = {
            ...channelObj,
          };
        } else {
          state.channels = {
            ...state.channels,
            ...channelObj,
          };
        }
        state.trackers = action.payload.trackers;
        state.error = null;
        state.loading = false;
      }
    );
    builder.addCase(fetchAsyncChannelsAndTrackers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(updateAsyncChannelPinStatus.fulfilled, (state, action) => {
      //
      state.channels = {
        ...state.channels,
        [action.payload.channelId]: action.payload.channel,
      };
      state.pinnedChannelList = action.payload.pinnedChannelList;
      state.sortedChannelList = action.payload.sortedChannelList;
    });
    builder.addCase(fetchAsynDatapiece.fulfilled, (state, action) => {
      if (action.payload !== null) {
        if (action.payload.channelType == 0) {
          state.cachedPivot = {
            ...state.cachedPivot,
            [`${action.payload.referenceId}`]: action.payload
              .datapiece as PivotTable,
          };
        }
        if (action.payload.channelType == 1) {
          state.cachedChart = {
            ...state.cachedChart,
            [`${action.payload.referenceId}`]: action.payload
              .datapiece as Chart,
          };
        }
      }
    });
    // builder.addCase(fetchAsyncTrackerByDate.fulfilled, (state, action) => {
    //   state.trackerCachedList = [...state.trackers];
    //   state.trackers = action.payload.filteredTracker;
    //   state.trackerFilterStatus = true;
    // });
  },
});

export const {
  addTempChannel,
  createChannel,
  updateChannel,
  addChannels,
  changeCurrentChannel,
  addMessages,
  addSingleMessageReceived,
  resetFirstScroll,
  addFilteredChannels,
  applyFilters,
  resetFromCollaboration,
  deleteChannelFromMenu,
  resetIsisActiveChannelDeleted,
  resetTrackerFilterStatus,
  changeClickOutsideStatus,
  addAllCollaborators,
  setActiveTrackerId,
  setNewChannelonChartId,
  deleteChannelFromNotification,
  setIsCollaboration,
  addNewChannel,
  setNewChannelTrigger,
  addDemoChannel,
  resetNewChannelOnChartId,
} = ChatSlice.actions;

export const ChatSliceReducer = ChatSlice.reducer;
